import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, Typography, useTheme, useMediaQuery } from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PreviewImage from "./previewImage";


const ImageUploaded = ({files, resetFile, resetGradable, eye, setDisabledProceed, onReupload}) => {
    const [open, setOpen] = useState(false);
    const [imageSrc, setImageSrc] = useState('');
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Detect Safari browser and device capabilities
    const isSafari = useMemo(() => {
        return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    }, []);
    
    // Detect if device has limited memory (older devices like iPhone SE)
    // IMPORTANT: Safari iOS has aggressive memory management - always use blob URLs, never data URLs
    const hasLimitedMemory = useMemo(() => {
        // Check device memory if available
        if (navigator.deviceMemory) {
            return navigator.deviceMemory <= 2; // 2GB or less
        }
        // Check for older iPhone models (iPhone SE, iPhone 6, etc.)
        const userAgent = navigator.userAgent;
        const isOldDevice = /iPhone.*OS [0-9]|iPhone.*OS 1[0-4]/.test(userAgent) || 
                           /iPhone SE/i.test(userAgent) ||
                           /iPhone.*OS 15/.test(userAgent) ||
                           /iPhone.*OS 16/.test(userAgent) ||
                           /iPhone.*OS 17/.test(userAgent) ||
                           /iPhone.*OS 18/.test(userAgent); // All iOS versions on Safari can have memory issues
        // ALWAYS use blob URLs on Safari iOS - data URLs cause crashes
        const isSafariIOS = /safari/i.test(userAgent) && !/chrome/i.test(userAgent) && /iPhone|iPad|iPod/i.test(userAgent);
        return isOldDevice || isSafariIOS; // Always treat Safari iOS as limited memory
    }, []);

    // Get the file object from props
    const fileObj = useMemo(() => {
        const obj = Array.isArray(files) ? files[0]?.file : files;
        return obj;
    }, [files, eye]);

    // Convert file to image source with proper cleanup
    useEffect(() => {
        let blobUrl = null;
        let dataUrlPromise = null;
        let fileReader = null;
        let isCancelled = false; // Flag to prevent state updates after unmount
        let currentFileObj = fileObj; // Store current fileObj to check in cleanup

        const createImageSrc = async () => {
            setImageLoading(true);
            setImageError(false);
            
            if (!fileObj) {
                if (!isCancelled) {
                    setImageSrc('');
                    setImageLoading(false);
                }
                return;
            }
            
            // If it's already a URL (string), use it directly
            if (typeof fileObj === 'string') {
                if (!isCancelled) {
                    setImageSrc(fileObj);
                    setImageLoading(false);
                }
                return;
            }
            
            // CRITICAL: Check for File/Blob FIRST - before any other checks!
            // File objects don't have enumerable keys, so Object.keys() returns []
            // This was causing early return before blob URL creation!
            if (fileObj instanceof File || fileObj instanceof Blob) {
                try {
                    blobUrl = URL.createObjectURL(fileObj);
                    if (!isCancelled) {
                        setImageSrc(blobUrl);
                        setImageLoading(false);
                    }
                    return;
                } catch (error) {
                    if (!isCancelled) {
                        setImageError(true);
                        setImageLoading(false);
                    }
                    return;
                }
            }
            
            // Handle ArrayBuffer format (new Safari-compatible format)
            if (fileObj && typeof fileObj === 'object' && fileObj.data && fileObj.type) {
                try {
                    const blob = new Blob([fileObj.data], { type: fileObj.type });
                    blobUrl = URL.createObjectURL(blob);
                    if (!isCancelled) {
                        setImageSrc(blobUrl);
                        setImageLoading(false);
                    }
                    return;
                } catch (error) {
                    // Failed to create blob from ArrayBuffer
                }
            }
            
            // If it's an object with a file property, try to get the file
            if (fileObj && typeof fileObj === 'object' && fileObj.file) {
                if (fileObj.file instanceof File || fileObj.file instanceof Blob) {
                    try {
                        blobUrl = URL.createObjectURL(fileObj.file);
                        if (!isCancelled) {
                            setImageSrc(blobUrl);
                            setImageLoading(false);
                        }
                        return;
                    } catch (error) {
                        // Failed to create blob URL from nested file
                    }
                }
            }
            
            // Fallback - return empty string
            if (!isCancelled) {
                setImageSrc('');
                setImageError(true);
                setImageLoading(false);
            }
        };

        createImageSrc();

        // Cleanup: revoke blob URL and cancel FileReader when component unmounts or file changes
        return () => {
            isCancelled = true; // Mark as cancelled to prevent state updates
            
            // Cancel FileReader if it's still reading
            if (fileReader && fileReader.readyState === FileReader.LOADING) {
                try {
                    fileReader.abort();
                } catch (error) {
                    // Ignore errors
                }
            }
            
            // Always revoke blob URL on cleanup (file change or unmount) to avoid memory leaks.
            // Short delay when file changed so next effect can create new URL first.
            if (blobUrl) {
                const urlToRevoke = blobUrl;
                setTimeout(() => {
                    try {
                        URL.revokeObjectURL(urlToRevoke);
                    } catch (error) {
                        // Ignore errors
                    }
                }, 100);
            }
        };
    }, [fileObj, files, isSafari]); // Add files to dependency to trigger when array changes

    const handlePreviewOpen = () => {
        setOpen(true);
    };

    const handlePreviewClose = () => {
        setOpen(false);
    };

    const handleReUpload = () => {
        // Call the onReupload function passed from parent to trigger file selection
        if (onReupload) {
            onReupload();
        } else {
            // Fallback to original behavior if onReupload is not provided
            resetFile(null);
            resetGradable();
            setDisabledProceed(true);
        }
    }

  const handleImageError = (e) => {
    setImageError(true);
    setImageLoading(false);
    
    // Try to recreate image source if it failed
    if (fileObj && (fileObj instanceof File || fileObj instanceof Blob)) {
      setImageLoading(true);
      setImageSrc('');
      setTimeout(() => {
        const fileSize = fileObj.size || 0;
        const shouldUseBlobUrl = hasLimitedMemory || fileSize > 5 * 1024 * 1024;
        
        if (shouldUseBlobUrl && (fileObj instanceof File || fileObj instanceof Blob)) {
          try {
            const newBlobUrl = URL.createObjectURL(fileObj);
            setImageSrc(newBlobUrl);
            setImageError(false);
            setImageLoading(false);
          } catch (error) {
            // Failed to recreate blob URL
          }
        } else if (isSafari) {
          // CRITICAL: Never use data URLs on Safari iOS - always use blob URLs
          try {
            const newBlobUrl = URL.createObjectURL(fileObj);
            setImageSrc(newBlobUrl);
            setImageError(false);
            setImageLoading(false);
          } catch (error) {
            setImageError(true);
            setImageLoading(false);
          }
        } else {
          try {
            const newBlobUrl = URL.createObjectURL(fileObj);
            setImageSrc(newBlobUrl);
            setImageError(false);
            setImageLoading(false);
          } catch (error) {
            setImageError(true);
            setImageLoading(false);
          }
        }
      }, 100);
    }
  };

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  // Don't render if no file object
  if (!fileObj) {
    return null;
  }

  return (
    <div style={{display: "flex", justifyItems: "center", alignItems: "center", padding: isMobile ? "5px" : "10px", flexDirection: isMobile ? "column":"row"}}>
        <div style={{ width: isMobile?"25vw":"7.5vw", height: isMobile?"25vw":"12.5vh", borderRadius: "4px", position: "relative"}}>
            {imageLoading && !imageSrc ? (
              <div style={{ 
                borderRadius: "4px", 
                width: "100%", 
                height: "100%", 
                display: "flex", 
                flexDirection: "column",
                alignItems: "center", 
                justifyContent: "center",
                background: "#f5f5f5",
                color: "#666",
                fontSize: "10px",
                textAlign: "center",
                padding: "5px"
              }}>
                <div style={{ marginBottom: "5px" }}>⏳</div>
                <div>Loading image...</div>
              </div>
            ) : imageError ? (
              <div style={{ 
                borderRadius: "4px", 
                width: "100%", 
                height: "100%", 
                display: "flex", 
                flexDirection: "column",
                alignItems: "center", 
                justifyContent: "center",
                background: "#fee",
                color: "#c33",
                fontSize: "10px",
                textAlign: "center",
                padding: "5px"
              }}>
                <div style={{ marginBottom: "5px" }}>❌</div>
                <div>Image failed to load.</div>
                <div style={{ fontSize: "8px", marginTop: "3px" }}>Please reupload.</div>
              </div>
            ) : imageSrc ? (
              <img 
                src={imageSrc} 
                onError={handleImageError}
                onLoad={handleImageLoad}
                style={{ 
                  borderRadius: "4px", 
                  width: "100%", 
                  height: "100%", 
                  objectFit: "contain",
                  opacity: imageLoading ? 0.5 : 1,
                  transition: "opacity 0.3s"
                }}
                alt="Uploaded eye image"
              />
            ) : (
              <div style={{ 
                borderRadius: "4px", 
                width: "100%", 
                height: "100%", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                background: "#f0f0f0",
                color: "#999",
                fontSize: "10px",
                textAlign: "center",
                padding: "5px"
              }}>
                No image source
              </div>
            )}
        </div>
        <Box marginLeft={1}>
            {/* <Typography fontSize="11px" fontWeight={400}>Image Uploaded!</Typography>
            {!isMobile && <Typography fontSize="8px" fontWeight={400} color="#404040">View image to confirm image quality.</Typography>} */}
            <Box
            sx={{
                display: 'flex',
                // flexDirection: isMobile ? 'column' : 'row',
                flexDirection: 'column',
                justifyContent: isMobile ? 'center' : 'flex-start',
                alignItems: isMobile ? 'center' : 'flex-start',
                height: isMobile ? '80%' : 'auto',
                width: '100%',
                gap: isMobile ? 0.5 : 1,
            }}
        >
            
            <Button
                onClick={() => handlePreviewOpen()}
                startIcon={<OpenInFullIcon fontSize="7px" style={{ color: "#0D1E83" }} />}
                sx={{
                    order: isMobile ? 1 : 2,
                    color: "#404040",
                    border: "1px solid #0000000D",
                    fontSize: "9px",
                    fontWeight: "400",
                    borderRadius: "4px",
                    marginTop: isMobile ? "5px" : 0,
                    whiteSpace: "nowrap",
                    textTransform: "none"
                }}
            >
                View image
            </Button>
            <Button
                onClick={() => handleReUpload()}
                startIcon={<CloudUploadIcon fontSize="7px" style={{ color: "#0D1E83" }} />}
                sx={{
                    order: isMobile ? 2 : 1,
                    color: "#404040",
                    border: "1px solid #0000000D",
                    fontSize: "9px",
                    fontWeight: "400",
                    borderRadius: "4px",
                    marginTop: isMobile ? "5px" : 0,
                    textTransform: "none"
                }}
            >
                Reupload image
            </Button>
        </Box>
        </Box>
        {open && <PreviewImage open={open} handlePreviewClose={handlePreviewClose} files={files} eye={eye}/>}
    </div>
  )
}

export default ImageUploaded